import { ReportNotes } from 'src/models/reports';
import { ReportComment } from 'src/services/reports.service';

export interface StudentDraft extends ReportNotes {
  id: number;
  result?: ReportComment & { characterLimit: number };
  error?: string;
}

interface ReportSession {
  students: StudentDraft[];
  characterLimit: string;
  expiresAt: number;
}

const key = 'gymea-report-signin-draft';
const maxAge = 30 * 60 * 1000;

const isStudent = (value: unknown): value is StudentDraft => {
  if (!value || typeof value !== 'object') return false;
  const student = value as Partial<StudentDraft>;
  return Number.isInteger(student.id) && Number(student.id) > 0
    && typeof student.name === 'string' && student.name.length <= 100
    && typeof student.notes === 'string' && student.notes.length <= 10000
    && ['they/them', 'she/her', 'he/him'].includes(student.pronouns || '')
    && Array.isArray(student.selectedComments) && student.selectedComments.every(idea => typeof idea === 'string')
    && (!student.result || (typeof student.result.studentName === 'string'
      && typeof student.result.comment === 'string' && student.result.comment.length <= 10000
      && Number.isInteger(student.result.characterLimit) && student.result.characterLimit >= 100 && student.result.characterLimit <= 5000));
};

// Only save when the teacher chooses to leave for authentication. No student
// content goes into the URL, Auth0 state, localStorage or server storage.
export const saveReportSession = (students: StudentDraft[], characterLimit: string) => {
  sessionStorage.setItem(key, JSON.stringify({ students, characterLimit, expiresAt: Date.now() + maxAge }));
};

export const clearReportSession = () => {
  try { sessionStorage.removeItem(key); } catch { /* Storage can be disabled by the browser. */ }
};

export const loadReportSession = (): ReportSession | null => {
  try {
    const saved: Partial<ReportSession> = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (!saved || typeof saved.expiresAt !== 'number' || saved.expiresAt <= Date.now()
      || typeof saved.characterLimit !== 'string' || saved.characterLimit.length > 5
      || !Array.isArray(saved.students) || !saved.students.length || !saved.students.every(isStudent)
      || new Set(saved.students.map(student => student.id)).size !== saved.students.length) {
      clearReportSession();
      return null;
    }
    return saved as ReportSession;
  } catch {
    clearReportSession();
    return null;
  }
};
